import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { X, ArrowRight } from '@phosphor-icons/react'
import { useAuth } from '../contexts/AuthContext'

/**
 * Barra de progreso del perfil.
 *
 * El onboarding se quedó en una pregunta, así que el resto del perfil se
 * completa desde aquí. La regla del texto: decir qué mejora al responder, no
 * qué falta por responder. Un perfil a medias no es una tarea pendiente, es
 * calidad de generación sin estrenar.
 *
 * Enseña una sola pregunta cada vez, la siguiente sin responder.
 */

// Qué gana el usuario respondiendo cada campo, y en qué pestaña del perfil
// vive la pregunta.
const FIELD_HELP = {
  creator_name: {
    tab: 'identidad',
    benefit: 'Con tu nombre, la IA te escribe a ti y no a un usuario.',
  },
  creator_type: {
    tab: 'identidad',
    benefit: 'Decir qué tipo de creador eres afina las ideas que te propone.',
  },
  project_name: {
    tab: 'identidad',
    benefit: 'Nombrar tu proyecto evita que los guiones lo llamen «mi proyecto».',
  },
  project_description: {
    tab: 'identidad',
    benefit: 'Contar de qué va tu proyecto le da a los guiones algo concreto de lo que tirar.',
  },
  target_audience: {
    tab: 'audiencia',
    benefit: 'Describir a tu audiencia es lo que hace que los guiones dejen de sonar genéricos.',
  },
  tone: {
    tab: 'audiencia',
    benefit: 'Elegir tu tono separa un guion tuyo de uno de plantilla.',
  },
  primary_platform: {
    tab: 'plataformas',
    benefit: 'Tu plataforma principal decide el ritmo y la duración de lo que se escribe.',
  },
  platforms: {
    tab: 'plataformas',
    benefit: 'Saber dónde publicas permite adaptar el mismo guion a cada sitio.',
  },
}

const DISMISS_KEY = 'rdk.profile_progress.dismissed'

export default function ProfileProgress() {
  const { profileCompleteness } = useAuth()
  const { pathname } = useLocation()
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1'
    } catch {
      return false
    }
  })

  // Se oculta sola al llegar al final. Y mientras carga no parpadea.
  if (!profileCompleteness || profileCompleteness.percent >= 100 || dismissed) return null
  // En el propio perfil sobra: el usuario ya está donde la barra le mandaría.
  if (pathname.startsWith('/profile')) return null

  const next = profileCompleteness.missing[0]
  const help = next && FIELD_HELP[next.field]
  if (!help) return null

  const dismiss = () => {
    setDismissed(true)
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // sin persistencia, vuelve en la siguiente navegación
    }
  }

  return (
    <div
      className="flex items-center gap-4 px-6 lg:px-8 py-2.5 bg-ds-surface border-b border-ds-border shrink-0"
      role="status"
    >
      <div className="flex items-center gap-2.5 shrink-0">
        <div className="w-16 h-0.5 bg-ds-border overflow-hidden" style={{ borderRadius: 1 }}>
          <div
            className="h-full bg-ds-red transition-all duration-500"
            style={{ width: `${profileCompleteness.percent}%` }}
          />
        </div>
        <span className="type-caption text-ds-muted tabular-nums">
          {profileCompleteness.percent}%
        </span>
      </div>

      <p
        className="flex-1 min-w-0 text-ds-muted text-[13px] leading-snug truncate"
        style={{ fontFamily: 'Barlow, sans-serif' }}
        title={help.benefit}
      >
        {help.benefit}
      </p>

      <Link
        to={`/profile?tab=${help.tab}`}
        className="shrink-0 flex items-center gap-1.5 type-label text-ds-text hover:text-ds-red transition-colors no-underline"
      >
        {next.label}
        <ArrowRight size={12} weight="bold" />
      </Link>

      <button
        type="button"
        onClick={dismiss}
        aria-label="Ocultar hasta la próxima sesión"
        title="Ocultar hasta la próxima sesión"
        className="shrink-0 text-ds-faint hover:text-ds-text transition-colors"
      >
        <X size={13} weight="bold" />
      </button>
    </div>
  )
}
